/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import {
    Abort,
    AbortError,
    createLogPrefix,
    type LogTarget,
    logDebug,
    type ReadonlyAbort,
    type Unsubscribe,
    withAbort,
} from '@amazon/vinyl-util'
import { callInit, type InitResponse } from '@/blackbolt/agent/claw/clawInit'
import { buildWsAuthParams, openSocket } from '@/blackbolt/agent/claw/clawSocket'
import { onBecameVisible } from '@/blackbolt/chat/visibilityGate'
import { documentEvents } from '@/util/interaction'

// Keeps ONE claw SESSION warm across chat turns.
//
// Beta resonance force-closes the `/agents/claw/ws` socket ~75ms after every
// turn's terminal `complete` frame (verified live — the close is server-driven;
// a client heartbeat can't stop it, which is why the per-turn heartbeat alone
// didn't fix the per-turn init). So a socket genuinely can't be held open turn
// to turn. What CAN be held is the SESSION: the init POST validates auth and
// sets the pod-affinity cookie the WS handshake rides, and that cookie persists
// in the browser cookie jar. We therefore run init exactly ONCE per session and
// reopen the WS per turn WITHOUT re-init — the reopened socket rides the cached
// cookie onto the same pod. This is RewindApp's model: reconnect the transport,
// keep the session. Init is reset on `close()` / sign-out and on a 30-min idle
// teardown (a fully idle session re-inits fresh rather than riding a stale
// pod-affinity cookie).

const target: LogTarget = { logPrefix: createLogPrefix('clawConnectionManager') }

// A long turn can run tools for many seconds; ping every 25s (RewindApp's
// transport contract) so the gateway doesn't drop the socket mid-turn and so an
// idle socket pre-warmed on chat-input focus survives until the first message.
const HEARTBEAT_INTERVAL_MS = 25_000

// Recycle an idle warm socket older than this so the reopened socket rebuilds
// its auth envelope, bounding how long a socket rides one OAuth token (well
// under the ~1h TTL). Auth is checked only at the WS upgrade, so this is
// defense-in-depth for a backend that (unlike beta) doesn't force-close per turn.
const MAX_SOCKET_AGE_MS = 5 * 60_000

// Tear the whole SESSION down after 30 min of no turn activity: stop the
// heartbeat, close the socket, AND drop the memoized init so the next warm/
// acquire re-runs `/agents/claw/init` for a fresh pod-affinity cookie. Stronger
// than MAX_SOCKET_AGE_MS (which recycles only the socket and keeps the init) —
// a long-abandoned session should not pin one pod forever. Every real turn
// (`acquire` / lease release), general user interactivity, and the window
// becoming visible all push the deadline out; the timer defers past an
// in-flight turn rather than tearing a socket out from under it.
const IDLE_TEARDOWN_MS = 30 * 60_000

/** A live warm connection plus the session id init handed back when it opened. */
export interface ClawConnection {
    readonly socket: WebSocket
    readonly initSessionId: string | undefined
}

/**
 * How a turn obtained its socket (diagnostic only):
 *   - `warm`      — rode an already-OPEN cached socket (e.g. one pre-warmed on
 *                   focus, or a rare same-tick second turn before the server
 *                   close lands).
 *   - `coalesced` — joined a socket open still in flight for a peer.
 *   - `fresh`     — opened a brand-new WS (init is skipped when already cached).
 */
export type ConnectionOrigin = 'warm' | 'coalesced' | 'fresh'

/**
 * How a turn ends its hold on the socket:
 *   - `reuse`   — the turn drained to its wire boundary; the socket is idle and
 *                 safe to keep warm (until the server closes it).
 *   - `discard` — the turn was aborted/abandoned mid-stream. The wire carries no
 *                 per-frame request id and no cancel frame, so the server keeps
 *                 streaming an untagged tail no next turn could skip or stop —
 *                 the socket must be torn down, never reused.
 */
export type TurnDisposition = 'reuse' | 'discard'

/**
 * A single turn's ref-counted hold on the shared connection. Exactly one
 * `release` per lease; the socket is closed only when the LAST active lease
 * releases with `discard` (or on `close()` / sign-out) — one turn's abort can
 * never tear a socket out from under a peer turn still streaming on it.
 */
export interface TurnLease {
    readonly connection: ClawConnection
    readonly origin: ConnectionOrigin
    release(disposition: TurnDisposition): void
}

/**
 * The two phases of bringing a claw connection up, split so the manager can run
 * `init` ONCE per session and `openSocket` per turn. The `abort` handed to each
 * is the manager's OWN lifetime signal (tied to `close()` / sign-out), NEVER a
 * single turn's abort — see the class doc for why the caller's abort must not
 * reach here.
 */
export interface ClawSessionOpener {
    /** Init POST — auth validation + pod-affinity cookie. Run once per session. */
    init(abort: ReadonlyAbort): Promise<InitResponse>
    /** Opens one WS socket. Rides the cookie the one init set; run per turn. */
    openSocket(abort: ReadonlyAbort): Promise<WebSocket>
}

const defaultOpener: ClawSessionOpener = {
    init: (abort) => callInit(abort),
    openSocket: async (abort) => {
        const auth = await buildWsAuthParams()
        return openSocket(abort, auth)
    },
}

/** Internal per-connection bookkeeping: the live socket plus its lease refcount. */
interface ConnRecord {
    readonly connection: ClawConnection
    /** Number of turns currently holding a lease on this socket. */
    active: number
    /** An aborted turn tainted the socket; close it once the last lease releases. */
    tainted: boolean
    /** Heartbeat interval keeping the idle socket warm; cleared on every teardown. */
    heartbeat: ReturnType<typeof setInterval> | undefined
    /** When the socket opened; drives the max-age auth-staleness recycle in `acquire`. */
    openedAt: number
}

/**
 * Persistent-session manager for the claw transport.
 *
 * Init caching is the crux (see the module header): `init` runs once per
 * session and the resulting pod-affinity cookie is reused by every subsequent
 * WS open, so a socket the server closed after a turn is reopened WITHOUT a
 * fresh init POST. The init promise is cached (`initPending`) and cleared only
 * on `close()` / sign-out or on an init failure (a failed init must not be
 * cached, or a transient 5xx would wedge the session).
 *
 * Abort ownership is the subtle part. A shared open (init or socket) must NOT be
 * driven by any single turn's `abort`: if it were, a turn that cancels
 * mid-connect would reject the shared open for EVERY concurrent waiter (and the
 * adapter swallows aborts, so healthy turns would silently vanish). Instead:
 *   - the shared open runs under the manager's own `lifetime` signal, which
 *     fires only on `close()` / sign-out — the one time every waiter SHOULD bail;
 *   - each caller races its OWN `abort` against the shared `pending` via
 *     `withAbort`, so a caller that cancels stops waiting without disturbing the
 *     shared open or its peers. The eventual socket is still cached, not wasted.
 *
 * Socket LIFECYCLE is likewise the manager's job, never a single turn's. Each
 * `acquire` hands back a ref-counted `TurnLease`; the socket is closed only when
 * the last active lease `release`s a `discard` (or on `close()`). A turn's abort
 * therefore tears down ONLY the shared cache slot, never a socket a peer turn is
 * still streaming on. (In this app turns are single-flight — the composer aborts
 * the prior turn before starting the next — so a live peer never actually
 * exists; the refcount is the structural guarantee that keeps it that way.)
 *
 * A monotonic `generation` guards against a stale opener installing a socket
 * after a `close()` landed mid-open, and makes a post-`close()` waiter throw
 * rather than return a torn-down connection.
 */
export class ClawConnectionManager {
    private pending: Promise<ConnRecord> | undefined
    private current: ConnRecord | undefined
    private initPending: Promise<InitResponse> | undefined
    private generation = 0
    private lifetime = new Abort()
    // Session-level idle timer: fires a full teardown (socket + init memo) after
    // IDLE_TEARDOWN_MS with no turn activity. Deliberately NOT a per-socket timer
    // like `heartbeat` — it must survive per-turn server closes so a session that
    // goes quiet after its socket dropped still drops its init. Cleared only on
    // session teardown (`close()`) or when it fires; every turn re-arms it.
    private idleTimer: ReturnType<typeof setTimeout> | undefined
    // General interactivity (pointerdown/keydown) + window-visible, wired only
    // while a session is live (attached alongside the idle timer's first arm,
    // detached on full teardown) so there is nothing to leak once the session
    // they protect is gone. See `attachActivityListeners`.
    private activityUnsub: Unsubscribe | undefined

    constructor(private readonly opener: ClawSessionOpener = defaultOpener) {}

    async acquire(abort: ReadonlyAbort | undefined): Promise<TurnLease> {
        const cur = this.current
        if (cur && cur.connection.socket.readyState === cur.connection.socket.OPEN) {
            // Recycle a socket past its max age so the reopen rebuilds fresh auth —
            // but only between turns (no active lease); never tear one out from
            // under an in-flight turn. A stale-but-busy socket rides one more turn.
            if (cur.active === 0 && Date.now() - cur.openedAt >= MAX_SOCKET_AGE_MS) {
                this.current = undefined
                this.stopHeartbeat(cur)
                this.closeQuietly(cur.connection.socket)
            } else {
                return this.lease(cur, 'warm')
            }
        }
        const generation = this.generation
        // A `pending` open we DIDN'T start means we're coalescing onto a peer's
        // in-flight open, not riding a warm socket — a distinct diagnostic.
        const coalescing = this.pending !== undefined
        const pending = this.pending ?? this.startOpen(generation)

        // Race the caller's OWN abort against the shared open — never forward it
        // into `startOpen`. A caller aborting here rejects only its own wait.
        const record = await withAbort(pending, abort)
        // A `close()` / sign-out during our wait tore the session down; the
        // socket we awaited is stale, so bail cleanly rather than hand it back.
        if (generation !== this.generation) throw new AbortError()
        return this.lease(record, coalescing ? 'coalesced' : 'fresh')
    }

    /**
     * Pre-warm the session on chat-input focus so the first message pays neither
     * the init POST nor the WS handshake. Fire-and-forget and idempotent: no-op
     * when a socket is already open or opening, so it's safe on the focus hot
     * path. A real `acquire` surfaces any failure — this best-effort warm
     * swallows its own rejection.
     */
    prewarm(): void {
        const cur = this.current
        if (cur && cur.connection.socket.readyState === cur.connection.socket.OPEN) return
        if (this.pending) return
        this.startOpen(this.generation).catch(() => {
            /* best-effort warm; the first real acquire re-runs and surfaces errors */
        })
    }

    /** Tear down on sign-out: bump generation, cancel any in-flight open, close current. */
    close(): void {
        this.generation++
        this.lifetime.abort()
        this.lifetime = new Abort()
        const cur = this.current
        this.current = undefined
        this.pending = undefined
        // Sign-out invalidates the auth/cookie the cached init stood for, so the
        // next session must re-init.
        this.initPending = undefined
        this.clearIdleTimer()
        this.detachActivityListeners()
        if (cur) {
            this.stopHeartbeat(cur)
            this.closeQuietly(cur.connection.socket)
        }
    }

    /** Take a ref-counted hold on `record`, returning the turn's lease. */
    private lease(record: ConnRecord, origin: ConnectionOrigin): TurnLease {
        record.active += 1
        // Turn activity — push the idle deadline out a full window.
        this.armIdleTimer()
        let released = false
        return {
            connection: record.connection,
            origin,
            release: (disposition: TurnDisposition): void => {
                if (released) return
                released = true
                if (disposition === 'discard') {
                    // Drop the tainted socket from the warm cache so the NEXT
                    // turn opens fresh instead of reading this turn's stale tail.
                    record.tainted = true
                    if (this.current === record) this.current = undefined
                }
                record.active -= 1
                // Close the actual socket only once no turn still holds it —
                // an abort must not close a socket a peer turn is streaming on.
                if (record.active <= 0 && record.tainted) {
                    this.stopHeartbeat(record)
                    this.closeQuietly(record.connection.socket)
                }
                // Turn boundary — re-arm so an idle session tears down 30 min
                // from the LAST turn, not from when the session first opened.
                this.armIdleTimer()
            },
        }
    }

    /**
     * Init POST, memoized for the session. Reused by every socket open so a
     * reopened WS skips it. A rejected init is NOT cached — the next open
     * retries rather than wedging the session on a transient failure.
     */
    private ensureInit(): Promise<InitResponse> {
        const existing = this.initPending
        if (existing) return existing
        const p = this.opener.init(this.lifetime).catch((err) => {
            if (this.initPending === p) this.initPending = undefined
            throw err
        })
        this.initPending = p
        return p
    }

    private startOpen(generation: number): Promise<ConnRecord> {
        // `Abort` implements `ReadonlyAbort`; the opener only reads it. Init is
        // memoized across opens; only the socket is opened per turn.
        const pending = this.ensureInit()
            .then(async (init) => {
                const socket = await this.opener.openSocket(this.lifetime)
                return { socket, initSessionId: init.sessionId } satisfies ClawConnection
            })
            .then((conn) => {
                if (generation !== this.generation) {
                    // A close()/sign-out landed mid-open — don't install a dead socket.
                    this.closeQuietly(conn.socket)
                    throw new AbortError()
                }
                const record: ConnRecord = {
                    connection: conn,
                    active: 0,
                    tainted: false,
                    heartbeat: undefined,
                    openedAt: Date.now(),
                }
                this.current = record
                conn.socket.addEventListener('close', () => this.invalidate(record), {
                    once: true,
                })
                this.startHeartbeat(record)
                // Arm the idle deadline as soon as a socket exists so even a
                // prewarmed-but-never-used session tears down after 30 min.
                this.armIdleTimer()
                this.attachActivityListeners()
                logDebug(target, 'connection opened')
                return record
            })
        this.pending = pending
        // Clear the shared slot when the SHARED open settles — NOT per-caller. A
        // caller that aborts its `withAbort` wait must not wipe an in-flight open
        // that other callers still ride.
        pending
            .finally(() => {
                if (this.pending === pending) this.pending = undefined
            })
            .catch(() => {
                /* the caller's await surfaces the failure; this chain is cleanup only */
            })
        return pending
    }

    private invalidate(record: ConnRecord): void {
        // The socket is gone whether or not it was still the current one, so the
        // timer must stop regardless of the current-record guard below. Init
        // stays cached — the whole point: the server closed this socket, but the
        // next turn reopens without a fresh init.
        this.stopHeartbeat(record)
        // Ignore a close event for a socket already replaced by a newer open.
        if (this.current !== record) return
        this.current = undefined
        logDebug(target, 'connection invalidated')
    }

    private startHeartbeat(record: ConnRecord): void {
        const { socket } = record.connection
        record.heartbeat = setInterval(() => {
            if (socket.readyState !== socket.OPEN) return
            try {
                socket.send(JSON.stringify({ type: 'ping' }))
            } catch {
                /* best-effort — a send on a racing-closed socket must not throw */
            }
        }, HEARTBEAT_INTERVAL_MS)
    }

    private stopHeartbeat(record: ConnRecord): void {
        if (record.heartbeat === undefined) return
        clearInterval(record.heartbeat)
        record.heartbeat = undefined
    }

    /** (Re)arm the idle-teardown deadline; any turn activity pushes it out. */
    private armIdleTimer(): void {
        this.clearIdleTimer()
        this.idleTimer = setTimeout(() => this.onIdleTimeout(), IDLE_TEARDOWN_MS)
    }

    private clearIdleTimer(): void {
        if (this.idleTimer === undefined) return
        clearTimeout(this.idleTimer)
        this.idleTimer = undefined
    }

    /**
     * External activity kick — user interactivity or the window becoming
     * visible — pushes the idle deadline out exactly like a turn does. Only
     * wired while `attachActivityListeners` has a session to protect.
     */
    private noteActivity(): void {
        this.armIdleTimer()
    }

    /**
     * Wire general user interactivity and window-visible as additional idle
     * kicks alongside the turn-based ones (`lease`/`release`). Idempotent;
     * attached once a session is truly live (first successful open) and
     * detached in `detachActivityListeners` on full teardown, so nothing
     * leaks once the session it protects is gone.
     */
    private attachActivityListeners(): void {
        if (this.activityUnsub) return
        const unsubPointer = documentEvents.on('pointerdown', () => this.noteActivity())
        const unsubKey = documentEvents.on('keydown', () => this.noteActivity())
        const unsubVisible = onBecameVisible(() => this.noteActivity())
        this.activityUnsub = () => {
            unsubPointer()
            unsubKey()
            unsubVisible()
        }
    }

    private detachActivityListeners(): void {
        this.activityUnsub?.()
        this.activityUnsub = undefined
    }

    private onIdleTimeout(): void {
        this.idleTimer = undefined
        const cur = this.current
        // A turn slipped in as the timer fired — defer past it exactly like the
        // max-age recycle; the next release re-arms with a full idle window.
        if (cur && cur.active > 0) {
            this.armIdleTimer()
            return
        }
        // Full teardown: drop the socket AND the memoized init so the next
        // warm/acquire re-inits fresh rather than riding the stale pod cookie.
        if (cur) {
            this.current = undefined
            this.stopHeartbeat(cur)
            this.closeQuietly(cur.connection.socket)
        }
        this.initPending = undefined
        this.detachActivityListeners()
    }

    // A socket close on an already-closing/closed socket can throw; teardown is
    // best-effort, so swallow it.
    private closeQuietly(socket: WebSocket): void {
        try {
            socket.close()
        } catch {
            /* best-effort */
        }
    }
}

/** Module singleton — one warm claw session shared across the app's chat turns. */
export const clawConnectionManager = new ClawConnectionManager()
